import { useEffect, useState } from 'react';
import type { Contact, Conversation } from '@vav/client-core';
import { Avatar } from './Avatar.tsx';
import { titleOf } from './Sidebar.tsx';
import { useT } from '../i18n.tsx';

/**
 * Pick where messages are going.
 *
 * Several destinations at once, because forwarding one thing to three people is
 * the normal case and doing it three times is the tax people actually notice.
 *
 * What it does not do is offer to add a comment. Forwarding sends what was
 * sent; anything else is a new message, and mixing the two makes it unclear to
 * the recipient which words were whose.
 */
export function ForwardDialog({
  conversations,
  contactsById,
  count,
  excludeId,
  onConfirm,
  onClose,
}: {
  conversations: Conversation[];
  contactsById: Record<string, Contact>;
  /** How many messages are being sent, for the confirm label. */
  count: number;
  /** The conversation they came from; forwarding to itself is not a thing. */
  excludeId?: string;
  onConfirm: (conversationIds: string[]) => void;
  onClose: () => void;
}) {
  const t = useT();
  const [filter, setFilter] = useState('');
  const [picked, setPicked] = useState<string[]>([]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const groupLabel = t('sidebar.group');
  const visible = conversations
    .filter((conversation) => conversation.id !== excludeId)
    .filter((conversation) =>
      titleOf(conversation, contactsById, groupLabel)
        .toLowerCase()
        .includes(filter.trim().toLowerCase()),
    );

  function toggle(id: string) {
    setPicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  return (
    <div className="overlay" onClick={onClose}>
      <div
        className="card dialog"
        role="dialog"
        aria-modal="true"
        aria-label={t('forward.title')}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="dialog__header">
          <h2>{t('forward.title')}</h2>
          <button className="icon" onClick={onClose} aria-label={t('bubble.closeImage')}>
            ✕
          </button>
        </header>

        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('sidebar.search')}
          aria-label={t('sidebar.search')}
          dir="auto"
          autoFocus
        />

        <div className="forward__list">
          {visible.length === 0 && <p className="empty muted">{t('sidebar.noMatches')}</p>}
          {visible.map((conversation) => {
            const title = titleOf(conversation, contactsById, groupLabel);
            const on = picked.includes(conversation.id);
            return (
              <button
                key={conversation.id}
                className={`forward__row ${on ? 'is-on' : ''}`}
                onClick={() => toggle(conversation.id)}
                aria-pressed={on}
              >
                <Avatar name={title} group={conversation.kind === 'group'} size={32} />
                <span>{title}</span>
                <span className="forward__check">{on ? '✓' : ''}</span>
              </button>
            );
          })}
        </div>

        <button
          className="primary"
          disabled={picked.length === 0}
          onClick={() => onConfirm(picked)}
        >
          {t('forward.send', { count, to: picked.length })}
        </button>
      </div>
    </div>
  );
}
